/**
 * Firestore reads whose empty results are EARNED (ISS-445).
 *
 * This app runs Firestore with `memoryLocalCache` — IndexedDB persistence is
 * deliberately forbidden (b815 queue poisoning, #3775; see config/firebase.ts).
 * Under that setup, when the SDK declares itself offline after its 10s backend
 * timeout ("Could not reach Cloud Firestore backend..."):
 *
 *   - `getDocs()` RESOLVES with whatever the in-memory cache holds — for a
 *     query this session never loaded, an EMPTY snapshot with
 *     `metadata.fromCache: true`. Callers that trust it render "you have no
 *     data" as if it were loaded (Joel's ISS-445: no customers, no history).
 *   - `getDoc()` on an uncached doc rejects with code 'unavailable' — or
 *     resolves fromCache-and-missing, indistinguishable from "deleted".
 *
 * The #4541 principle: an empty state must be earned by a successful server
 * read. These wrappers enforce it at the data layer:
 *
 *   - fromCache + empty/missing  -> throw DegradedReadError (retry UI)
 *   - fromCache + has data       -> serve it (cache fallback). NOTE: this
 *     resolves NORMALLY — the caller's query succeeds and carries no per-query
 *     staleness marker. The degradation is recorded only in the shared
 *     reachability monitor.
 *
 *     COVERED as of #4602: `components/shared/ConnectionStatusBadge` subscribes
 *     to that monitor and is mounted once in `ModuleLayout`, so this silent case
 *     now shows an app-wide "some of what you see may be out of date" bar. It is
 *     deliberately NOT per-query — no query object can observe this condition, so
 *     asking each surface to render it would be both duplicated and forgettable.
 *     The stale banners still own the different, per-query failed-REFRESH case.
 *   - server-confirmed anything  -> serve it (a real empty is a real answer)
 *
 * Every outcome is also reported into the shared reachability monitor so
 * degraded surfaces can auto-retry when the connection recovers.
 */
import {
  getDoc,
  getDocs,
  type DocumentData,
  type DocumentReference,
  type DocumentSnapshot,
  type Query,
  type QuerySnapshot,
} from 'firebase/firestore';
import { reachability } from '../reachability';

/** Firestore SDK error code for "client is offline / backend unreachable". */
const FIRESTORE_UNAVAILABLE = 'unavailable';

export class DegradedReadError extends Error {
  /**
   * Structural brand. Without it this class is structurally identical to
   * `Error`, and TypeScript's false-branch narrowing after
   * `isDegradedReadError(e)` then collapses an `Error | null` to `null` —
   * breaking every `e instanceof Error` that follows the guard.
   */
  readonly isDegradedRead = true;

  constructor(what: string) {
    super(
      `Could not reach the server to load ${what}. ` +
        'This usually means a weak connection — the data is still there.',
    );
    this.name = 'DegradedReadError';
  }
}

/**
 * Matches by name as well as instance so the guard survives module
 * duplication (two bundles, two copies of this class).
 */
export function isDegradedReadError(error: unknown): error is DegradedReadError {
  return error instanceof Error && error.name === 'DegradedReadError';
}

function isUnavailableError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { code?: unknown }).code === FIRESTORE_UNAVAILABLE
  );
}

/**
 * `getDocs` that never lets a cache-served empty impersonate a loaded empty.
 * @param what Human-readable description of the read, used in the error message.
 */
export async function getDocsEarned<T = DocumentData>(
  query: Query<T>,
  what = 'this list',
): Promise<QuerySnapshot<T>> {
  let snapshot: QuerySnapshot<T>;
  try {
    snapshot = await getDocs(query);
  } catch (error) {
    if (isUnavailableError(error)) {
      reachability.reportBackendUnreachable();
      throw new DegradedReadError(what);
    }
    throw error;
  }

  // `metadata` is always present on a real SDK snapshot; a stub without it is a server answer.
  if (snapshot.metadata?.fromCache) {
    reachability.reportBackendUnreachable();
    if (snapshot.empty) {
      // Nothing cached and no server answer — this "empty" is NOT earned.
      throw new DegradedReadError(what);
    }
    // Cache fallback: stale but real data beats a crash or a false empty.
    return snapshot;
  }

  reachability.reportBackendSuccess();
  return snapshot;
}

/**
 * `getDoc` that never lets "we couldn't reach the server" impersonate
 * "this document does not exist".
 * @param what Human-readable description of the read, used in the error message.
 */
export async function getDocEarned<T = DocumentData>(
  ref: DocumentReference<T>,
  what = 'this record',
): Promise<DocumentSnapshot<T>> {
  let snapshot: DocumentSnapshot<T>;
  try {
    snapshot = await getDoc(ref);
  } catch (error) {
    if (isUnavailableError(error)) {
      reachability.reportBackendUnreachable();
      throw new DegradedReadError(what);
    }
    throw error;
  }

  // `metadata` is always present on a real SDK snapshot; a stub without it is a server answer.
  if (snapshot.metadata?.fromCache) {
    reachability.reportBackendUnreachable();
    if (!snapshot.exists()) {
      // Absent from cache ≠ absent from the server.
      throw new DegradedReadError(what);
    }
    return snapshot;
  }

  reachability.reportBackendSuccess();
  return snapshot;
}
